/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import humanizeDuration from 'humanize-duration';
import Linkify from 'react-linkify';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import Localized from '../../../shared/components/Localized';
import MaintenanceStatus from './MaintenanceStatus.js';
import StartTime from './StartTime.js';
import EndTime from './EndTime.js';


import styles from '../../styles';
import { DATE_FORMAT, TIMESTAMP_FORMAT } from "../../../shared/utils/dates";

const formattingString = 'HH:mm z, MMM DD';

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding: 1rem;
`;

const LeftColumn = styled.div`
    width: 60%;

    @media (min-width: 768px) {
        border-right: 1px solid #d8dde6;
        padding-right: 1rem;
    }

    @media (max-width: 767px) {
        width: 100%;
        border-bottom: 1px solid #d8dde6;
        padding-bottom: 1rem;
    }
`;

const RightColumn = styled.div`
    width: 40%;

    @media (min-width: 768px) {
        padding-left: 1rem;
    }

    @media (max-width: 767px) {
        width: 100%;
        padding-top: 1rem;
    }
`;

const Item = styled.div`
    margin-bottom: .5rem;
`;

const Label = styled.div`
    ${styles.fonts.baseSmall};
`;

const Value = styled.div`
    ${styles.fonts.base};
    color: ${styles.colors.biscay};
`;

class MaintenanceInfo extends React.Component {
  static propTypes = {
    resolved: PropTypes.bool,
    serviceKeys: PropTypes.arrayOf(PropTypes.string),
    instanceKeys: PropTypes.arrayOf(PropTypes.string),
    startTime: PropTypes.object,
    endTime: PropTypes.object,
    message: PropTypes.object,
    additionalInformation: PropTypes.string,
    showDateOnly: PropTypes.bool,
    showAsWindowTime: PropTypes.bool
  }

  render() {
    const { resolved, status, instanceKeys, serviceKeys, durationMS, ongoing, missingTranslations } = this.props;
    const { startTime, endTime, plannedStartTime, plannedEndTime, message, additionalInformation, showPlannedDateOnly, showAsWindowTime } = this.props;
    const plannedOrWindow = showAsWindowTime ? 'window' : 'planned'
    const scheduledStartLabel = showPlannedDateOnly ? 'releaseWindowDate' : `${plannedOrWindow}StartTime`;
    const startTimeFormat = showPlannedDateOnly ? DATE_FORMAT : TIMESTAMP_FORMAT;
    const scheduledEndLabel = `${plannedOrWindow}EndTime`;
    return (
      <Wrapper>
        <LeftColumn>
          <Item>
            <Label>
              <Localized modelName={'modal'} modelKey={'status'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
            </Label>
            <Value>
              <MaintenanceStatus status={status} />
            </Value>
          </Item>
          <Item>
            <Label>
              <Localized modelName={'modal'} modelKey={'relatedInstances'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
            </Label>
            <Value>
              { instanceKeys.map((instanceKey, i) => {
                return (
                  <span key={i}>
                    <Link to = {'/instances/' + instanceKey}>
                      <Localized modelName={'instance'} modelKey={instanceKey} modelAttribute={'label'} missingTranslations={missingTranslations}/>
                    </Link>
                    { instanceKeys.length - 1 === i ? '' : ', '}
                  </span>
                );
              }) }
            </Value>
          </Item>
          <Item>
            <Label>
              <Localized modelName={'modal'} modelKey={'relatedServices'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
            </Label>
            <Value>
              { serviceKeys.map((serviceKey, i) => {
                return (
                  <span key={i}>
                    <Localized modelName={'service'} modelKey={serviceKey} modelAttribute={'label'} missingTranslations={missingTranslations}/>
                    { serviceKeys.length - 1 === i ? '' : ', '}
                  </span>
                );
              }) }
            </Value>
          </Item>
          <Item>
            <Label>
              <Localized modelName={'field'} modelKey={'maintenanceType'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
            </Label>
            <Value>
              <Localized modelName={'fieldOption'} modelKey={message.maintenanceType} modelAttribute={'value'} missingTranslations={missingTranslations}/>
            </Value>
          </Item>
          <Item>
            <Label>
              <Localized modelName={'field'} modelKey={'availability'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
            </Label>
            <Value>
              <Localized modelName={'fieldOption'} modelKey={message.availability} modelAttribute={'value'} missingTranslations={missingTranslations}/>
            </Value>
          </Item>
          { additionalInformation &&
            <Item>
              <Label>
                <Localized modelName={'modal'} modelKey={'additionalInformation'} modelAttribute={'label'} />
              </Label>
              <Value>
                <Linkify>
                  {additionalInformation.split('\\n').map((item, key) => {
                    return (
                      <span key={key}>
                        {item}
                        <br/>
                      </span>
                    );
                  }) }
                </Linkify>
              </Value>
            </Item>
          }
        </LeftColumn>
        <RightColumn>
          <Item>
            <StartTime
              startTime={startTime}
              plannedStartTime={plannedStartTime}
              ongoing={ongoing}
              scheduledLabel={scheduledStartLabel}
              format={startTimeFormat}/>
            { !showPlannedDateOnly &&
              <EndTime
                endTime={endTime}
                plannedEndTime={plannedEndTime}
                resolved={resolved}
                scheduledLabel={scheduledEndLabel}/>
            }
            { durationMS > 0 &&
              <Item>
                <Label>
                  <Localized modelName={'modal'} modelKey={'duration'} modelAttribute={'label'} missingTranslations={missingTranslations}/>
                </Label>
                <Value>
                  { humanizeDuration(durationMS, { largest: 2, round: true, units: ['y', 'mo', 'w', 'd', 'h', 'm'] }) }
                </Value>
              </Item>
            }
          </Item>
        </RightColumn>
      </Wrapper>
    );
  }
}

export default connect((state) => ({ timezone: state.timezonePicker }))(MaintenanceInfo);
